// src/components/leadsComponents/BulkSendModal.js
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchTemplates } from '../../store/thunks/templatesActions';
import { sendEmail } from '../../store/thunks/leadsActions';

const asAddress = (e) => (typeof e === 'string' ? e : e?.address || '');
const get = (obj, path, fallback = '') =>
  path.split('.').reduce((acc, key) => (acc && acc[key] != null ? acc[key] : null), obj) ?? fallback;

function mergeTemplate(text = '', lead) {
  if (!text) return '';
  return text.replace(/\{\{\s*([^}]+)\s*\}\}/g, (_, rawKey) => {
    const key = String(rawKey || '').trim();
    const map = {
      company_name: lead.company_name || '',
      owner_name: lead.owner_name || '',
      website: lead.website || '',
      'enrichment.property_count_estimate': get(lead, 'enrichment.property_count_estimate', 0),
      property_count_estimate: get(lead, 'enrichment.property_count_estimate', 0),
    };
    return (key in map ? map[key] : get(lead, key, '')) + '';
  });
}

const isRawText = (txt) => !/<[a-z][\s\S]*>/i.test(txt);
const toHtml = (body) =>
  isRawText(body)
    ? body
        .split('\n')
        .map((line, i) => (i > 0 ? `<br/>${line}` : line))
        .join('')
    : body;

export default function BulkSendModal({ leads = [], onClose }) {
  const dispatch = useDispatch();
  const { templates = [], loading } = useSelector((s) => s.templates);
  const userEmail = useSelector((s) => s.login?.adminInfo?.gmail) || '';

  const [sortOrder, setSortOrder] = useState(null); // 'asc' | 'desc' | null
  // selected: { [leadId]: { checked: boolean, emails: string[] } }
  const [selected, setSelected] = useState({});
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [previewLeadId, setPreviewLeadId] = useState('');
  const [sending, setSending] = useState(false);
  const [sentCount, setSentCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    dispatch(fetchTemplates());
  }, [dispatch]);

  // Only leads with at least one valid email
  const normalizedLeads = useMemo(() => {
    const result = [];
    for (const l of leads) {
      const raw = Array.isArray(l.emails) ? l.emails : [];
      const clean = raw
        .map(asAddress)
        .map((s) => String(s || '').trim())
        .filter(Boolean);
      if (clean.length > 0) {
        result.push({
          ...l,
          __emails: clean,
          __propCount: l.enrichment?.property_count_estimate ?? 0,
        });
      }
    }
    if (sortOrder) {
      result.sort((a, b) =>
        sortOrder === 'asc' ? a.__propCount - b.__propCount : b.__propCount - a.__propCount
      );
    }
    return result;
  }, [leads, sortOrder]);

  // Initialize selection for filtered leads
  useEffect(() => {
    const next = {};
    for (const l of normalizedLeads) {
      const first = l.__emails[0] || '';
      next[l._id] = { checked: false, emails: first ? [first] : [] };
    }
    setSelected(next);
    if (normalizedLeads[0]) setPreviewLeadId(normalizedLeads[0]._id);
  }, [normalizedLeads]);

  const allSelected = useMemo(() => {
    if (!normalizedLeads.length) return false;
    return normalizedLeads.every((l) => selected[l._id]?.checked);
  }, [normalizedLeads, selected]);

  const selectedCount = useMemo(
    () => normalizedLeads.filter((l) => selected[l._id]?.checked).length,
    [normalizedLeads, selected]
  );

  const selectedTemplate = useMemo(
    () => templates.find((t) => t._id === selectedTemplateId) || null,
    [templates, selectedTemplateId]
  );

  const previewLead = useMemo(
    () => normalizedLeads.find((l) => l._id === previewLeadId) || normalizedLeads[0],
    [normalizedLeads, previewLeadId]
  );

  const canSend = useMemo(() => {
    if (!selectedTemplateId || selectedCount === 0 || !userEmail) return false;
    for (const l of normalizedLeads) {
      const s = selected[l._id];
      if (s?.checked && (!Array.isArray(s.emails) || s.emails.length === 0)) return false;
    }
    return true;
  }, [selectedTemplateId, selectedCount, normalizedLeads, selected, userEmail]);

  const toggleAll = (value) => {
    const next = { ...selected };
    for (const l of normalizedLeads) {
      next[l._id] = { ...(next[l._id] || {}), checked: value };
    }
    setSelected(next);
  };

  const toggleEmailForLead = (leadId, email, isChecked) => {
    setSelected((prev) => {
      const cur = prev[leadId] || { checked: false, emails: [] };
      const set = new Set(cur.emails);
      if (isChecked) set.add(email);
      else set.delete(email);
      return { ...prev, [leadId]: { ...cur, emails: Array.from(set) } };
    });
  };

  const previewSubject = useMemo(() => {
    if (!selectedTemplate || !previewLead) return '';
    return mergeTemplate(selectedTemplate.subject || '', previewLead);
  }, [selectedTemplate, previewLead]);

  const previewBody = useMemo(() => {
    if (!selectedTemplate || !previewLead) return '';
    const source = selectedTemplate.body || selectedTemplate.html || '';
    return mergeTemplate(source, previewLead);
  }, [selectedTemplate, previewLead]);

  // Send using same thunk as EmailComposer — ONE EMAIL PER RECIPIENT
  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setSentCount(0);
    setErrorMsg('');

    try {
      for (const l of normalizedLeads) {
        const s = selected[l._id];
        if (!s?.checked) continue;

        // Merge per lead once
        const mergedSubject = mergeTemplate(selectedTemplate.subject || '', l);
        const mergedBody = mergeTemplate(
          selectedTemplate.body || selectedTemplate.html || '',
          l
        );
        const htmlBody = toHtml(mergedBody);
        const plainTextBody = mergedBody;

        // Send one dispatch per selected recipient
        for (const recipient of s.emails) {
          const to = String(recipient).trim();
          if (!to) continue;

          try {
            await dispatch(
              sendEmail({
                leadId: l._id,
                from: userEmail,
                to,                // single address (string) just like EmailComposer
                subject: mergedSubject,
                body: htmlBody,    // HTML
                text: plainTextBody, // Plain text
              })
            ).unwrap();

            setSentCount((c) => c + 1);
          } catch (err) {
            // Pull most helpful error text
            const data =
              err?.response?.data ??
              err?.data ??
              err?.message ??
              err;
            const readable =
              typeof data === 'string'
                ? data
                : JSON.stringify(data);
            console.error('Send failed:', readable);
            setErrorMsg(`Failed for ${to}: ${readable}`);
          }
        }
      }

      if (!errorMsg) onClose();
      setSending(false);
    } catch (outer) {
      const data =
        outer?.response?.data ??
        outer?.data ??
        outer?.message ??
        outer;
      const readable =
        typeof data === 'string' ? data : JSON.stringify(data);
      console.error('Bulk send failed:', readable);
      setErrorMsg(`Bulk send failed: ${readable}`);
      setSending(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      aria-modal="true"
      role="dialog"
    >
      <div className="bg-white w-[1080px] max-h-[88vh] rounded-[16px] shadow-[0_10px_40px_rgba(0,0,0,0.15)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#E5E7EB' }}>
          <div>
            <h3 className="text-xl font-semibold" style={{ color: '#111827' }}>
              Bulk Send Emails
            </h3>
            <p className="text-xs mt-1" style={{ color: '#6B7280' }}>
              {selectedCount} selected of {normalizedLeads.length} leads
              {sending ? ` • Sending ${sentCount}/${selectedCount}` : ''}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <span className="text-xs" style={{ color: '#6B7280' }}>Sort by properties:</span>
              <button
                className="px-2 py-1 rounded-[8px] text-xs"
                style={{
                  backgroundColor: sortOrder === 'asc' ? '#2563EB' : '#FFFFFF',
                  color: sortOrder === 'asc' ? '#FFFFFF' : '#374151',
                  border: '1px solid #D1D5DB',
                }}
                onClick={() => setSortOrder((o) => (o === 'asc' ? null : 'asc'))}
              >
                Asc ↑
              </button>
              <button
                className="px-2 py-1 rounded-[8px] text-xs"
                style={{
                  backgroundColor: sortOrder === 'desc' ? '#2563EB' : '#FFFFFF',
                  color: sortOrder === 'desc' ? '#FFFFFF' : '#374151',
                  border: '1px solid #D1D5DB',
                }}
                onClick={() => setSortOrder((o) => (o === 'desc' ? null : 'desc'))}
              >
                Desc ↓
              </button>
            </div>

            <button
              className="px-3 py-1 rounded-[10px] text-sm"
              style={{ backgroundColor: allSelected ? '#F3F4F6' : '#E5E7EB', color: '#111827' }}
              onClick={() => toggleAll(!allSelected)}
            >
              {allSelected ? 'Clear All' : 'Select All'}
            </button>
            <button
              className="px-3 py-1 rounded-[10px] text-sm"
              style={{ color: '#6B7280' }}
              onClick={onClose}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-2 gap-6 p-6 overflow-auto">
          {/* Left: leads with multi-email selection */}
          <div className="space-y-4">
            {/* Template chooser */}
            <div>
              <label className="text-sm font-medium block mb-1" style={{ color: '#374151' }}>
                Template
              </label>
              <select
                className="w-full px-3 py-2 rounded-[12px] border"
                style={{ borderColor: '#D1D5DB', color: '#111827', backgroundColor: '#FFFFFF' }}
                value={selectedTemplateId}
                onChange={(e) => setSelectedTemplateId(e.target.value)}
              >
                <option value="">Select Template</option>
                {loading ? (
                  <option>Loading...</option>
                ) : (
                  templates.map((t) => (
                    <option key={t._id} value={t._id}>
                      {t.name}
                    </option>
                  ))
                )}
              </select>
            </div>

            <div className="rounded-[12px] border overflow-hidden" style={{ borderColor: '#E5E7EB' }}>
              <div className="px-3 py-2 text-xs font-medium" style={{ backgroundColor: '#F9FAFB', color: '#6B7280' }}>
                Select recipients and choose emails for each lead
              </div>

              <div className="max-h-[48vh] overflow-auto divide-y" style={{ divideColor: '#E5E7EB' }}>
                {normalizedLeads.map((lead) => {
                  const s = selected[lead._id] || { checked: false, emails: [] };
                  return (
                    <div key={lead._id} className="p-3">
                      <div className="flex items-start justify-between gap-3">
                        <label className="flex items-start gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-[3px]"
                            checked={!!s.checked}
                            onChange={(e) =>
                              setSelected((prev) => ({
                                ...prev,
                                [lead._id]: { ...(prev[lead._id] || {}), checked: e.target.checked },
                              }))
                            }
                          />
                          <div>
                            <div className="text-sm font-medium" style={{ color: '#111827' }}>
                              {lead.company_name || 'Untitled'}
                            </div>
                            <div className="text-xs mt-0.5" style={{ color: '#6B7280' }}>
                              Properties: {lead.__propCount}
                            </div>
                          </div>
                        </label>

                        {/* Multi-email checkbox list */}
                        <div className="w-[60%]">
                          <label className="text-[11px] block mb-1" style={{ color: '#6B7280' }}>
                            Send to
                          </label>
                          <div className="rounded-[10px] border p-2" style={{ borderColor: '#D1D5DB' }}>
                            {lead.__emails.map((addr) => {
                              const checked = s.emails.includes(addr);
                              return (
                                <label key={addr} className="flex items-center justify-between py-1 cursor-pointer">
                                  <span className="text-sm" style={{ color: '#111827' }}>
                                    {addr}
                                  </span>
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={(e) => {
                                      toggleEmailForLead(lead._id, addr, e.target.checked);
                                      setPreviewLeadId(lead._id);
                                    }}
                                  />
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {normalizedLeads.length === 0 && (
                  <div className="p-6 text-sm text-center" style={{ color: '#6B7280' }}>
                    No leads with emails
                  </div>
                )}
              </div>
            </div>

            {/* Error message (if any) */}
            {errorMsg && (
              <div className="text-xs border rounded-[10px] p-2"
                   style={{ backgroundColor: '#FEF2F2', color: '#991B1B', borderColor: '#FECACA' }}>
                {errorMsg}
              </div>
            )}
          </div>

          {/* Right: Live Preview */}
          <div className="space-y-4">
            <div className="rounded-[12px] border" style={{ borderColor: '#E5E7EB' }}>
              <div className="px-3 py-2 text-xs font-medium" style={{ backgroundColor: '#F9FAFB', color: '#6B7280' }}>
                Live Preview
              </div>

              <div className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <label className="text-xs" style={{ color: '#6B7280' }}>Preview Lead</label>
                  <select
                    className="px-3 py-2 rounded-[10px] border"
                    style={{ borderColor: '#D1D5DB', color: '#111827', backgroundColor: '#FFFFFF' }}
                    value={previewLeadId}
                    onChange={(e) => setPreviewLeadId(e.target.value)}
                  >
                    {normalizedLeads.map((l) => (
                      <option key={l._id} value={l._id}>
                        {l.company_name || 'Untitled'}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="rounded-[12px] border p-3" style={{ borderColor: '#E5E7EB', backgroundColor: '#FFFFFF' }}>
                  <h4 className="text-sm font-semibold mb-2" style={{ color: '#1F2937' }}>
                    {previewSubject || 'Select a template to preview'}
                  </h4>

                  {previewBody ? (
                    <div
                      className="text-sm"
                      style={{ color: '#374151', whiteSpace: 'pre-line' }}
                      dangerouslySetInnerHTML={{ __html: previewBody }}
                    />
                  ) : (
                    <p className="text-sm" style={{ color: '#6B7280' }}>
                      Choose a template to see the merged preview here
                    </p>
                  )}
                </div>

                <div className="text-[11px] rounded-[10px] p-3" style={{ backgroundColor: '#F3F4F6', color: '#4B5563' }}>
                  You can use variables like:
                  <code>{' {{company_name}} {{owner_name}} {{website}} {{enrichment.property_count_estimate}}'}</code>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t" style={{ borderColor: '#E5E7EB' }}>
          <div className="text-xs" style={{ color: '#6B7280' }}>
            {selectedCount} lead{selectedCount === 1 ? '' : 's'} selected
          </div>
          <div className="flex gap-3">
            <button
              className="px-4 py-2 rounded-[12px] border"
              style={{ borderColor: '#D1D5DB', color: '#374151', backgroundColor: '#FFFFFF' }}
              onClick={onClose}
              disabled={sending}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 rounded-[12px]"
              style={{ backgroundColor: canSend ? '#2563EB' : '#93C5FD', color: '#FFFFFF' }}
              disabled={!canSend || sending}
              onClick={handleSend}
            >
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
